import { useState } from "react";
import { useLazyGetAttachedDocumentQuery } from "../api";
import { canOpenInViewer } from "../utils";
import type { AttachedDocumentListItem } from "../types";
import type { PromptState } from "./useAttachedDocumentsModals";

export interface ViewerState {
  type: "pdf" | "image";
  fileUrl: string;
  fileName: string;
}

export function useAttachedDocumentsViewer(
  setPrompt: (state: PromptState | null) => void,
  t: (key: string) => string,
) {
  const [viewer, setViewer] = useState<ViewerState | null>(null);
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const [fetchDocument] = useLazyGetAttachedDocumentQuery();

  async function openViewer(record: AttachedDocumentListItem) {
    if (!canOpenInViewer(record.content_type) || loadingId !== null) return;
    try {
      setLoadingId(record.id);
      const doc = await fetchDocument(record.id).unwrap();
      if (!doc.file_url) {
        setPrompt({
          title: t("messages.errorTitle"),
          text: t("messages.openErrorMissingUrl"),
          variant: "error",
        });
        return;
      }
      setViewer({
        type: record.content_type === "application/pdf" ? "pdf" : "image",
        fileUrl: doc.file_url,
        fileName: doc.file_name,
      });
    } catch {
      setPrompt({
        title: t("messages.errorTitle"),
        text: t("messages.openErrorText"),
        variant: "error",
      });
    } finally {
      setLoadingId(null);
    }
  }

  function closeViewer() {
    setViewer(null);
  }

  return { viewer, loadingId, openViewer, closeViewer };
}
